import React, { useState, useCallback, useEffect } from 'react';
import {
    TextInput,
    NumberInput,
    Stack,
    Group,
    Button,
    Select,
    Switch,
    Text,
    SegmentedControl,
    Textarea,
    Card,
    Tooltip,
    ActionIcon,
    Divider,
    Box,
    Paper,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useDropzone } from 'react-dropzone';
import { env } from '../../../utils/env';
import { GroupMember } from '../../../types/group';
import { Expense } from './AddExpenseModal';
import { CategorySelector } from './CategorySelector';
import { SplitControls } from './SplitControls';
import { ImageUploader } from './ImageUploader';
import { AdditionalPayersControl } from './AdditionalPayersControl';
import { Calculator } from './Calculator';

// Icons
import uploadIcon from '../../../assets/icons/upload.svg';
import calculatorIcon from '../../../assets/icons/calculator.svg';
import clockIcon from '../../../assets/icons/clock.svg';
import userIcon from '../../../assets/icons/user.svg';
import multiplePayersIcon from '../../../assets/icons/users.svg';
import saveIcon from '../../../assets/icons/save.svg';

interface FormValues {
    title: string;
    amount: number;
    date: Date;
    due_date: Date | null;
    paid_by_id: string;
    split_type: 'equal' | 'exact' | 'percentage' | 'shares' | 'items';
    split_details: Record<string, number | any>;
    additional_payers: Record<string, { amount: number }> | null;
    category_id?: string;
    notes?: string;
    recurring: boolean;
    recurring_frequency?: string;
    recurring_end_date?: Date | null;
    receipt_images: File[];
    status: 'active' | 'draft';
}

interface InitialData {
    description?: string;
    amount?: number;
    date?: Date;
    receipt_image_url?: string;
}

export interface ExpenseFormProps {
    groupId: string;
    members: GroupMember[];
    accessToken: string;
    expense?: Expense;
    onClose: () => void;
    onSuccess: () => void;
    setIsSubmitting: (value: boolean) => void;
    initialData?: InitialData;
}

export const ExpenseForm = ({
    groupId,
    members,
    accessToken,
    expense,
    onClose,
    onSuccess,
    setIsSubmitting,
    initialData
}: ExpenseFormProps) => {
    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [existingImages, setExistingImages] = useState<string[]>([]);
    const [removedImageIds, setRemovedImageIds] = useState<string[]>([]);
    const [showMultiplePayers, setShowMultiplePayers] = useState(false);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [splitTypeTouched, setSplitTypeTouched] = useState(false);
    const [amountDebounced, setAmountDebounced] = useState(0);
    const [disableSubmit, setDisableSubmit] = useState(false);

    const isEditing = !!expense;

    // Helper to format date for API
    const formatDateForApi = (date: Date): string => {
        return date.toISOString();
    };

    // Set default form values
    const getDefaultValues = (): FormValues => {
        // Start with basic defaults
        const defaults: FormValues = {
            title: '',
            amount: 0,
            date: new Date(),
            due_date: null,
            paid_by_id: '',
            split_type: 'equal',
            split_details: {},
            additional_payers: null,
            category_id: '',
            notes: '',
            recurring: false,
            recurring_frequency: '',
            recurring_end_date: null,
            receipt_images: [],
            status: 'active',
        };

        // Override with initialData if provided
        if (initialData) {
            if (initialData.description) defaults.title = initialData.description;
            if (initialData.amount) defaults.amount = initialData.amount;
            if (initialData.date) defaults.date = initialData.date;

            // Set preview image if receipt_image_url is provided
            if (initialData.receipt_image_url) {
                setPreviewImages([initialData.receipt_image_url]);
            }
        }

        return defaults;
    };

    // Initialize form with default values
    const form = useForm<FormValues>({
        initialValues: getDefaultValues(),
        validate: {
            title: (value: string) => (!value.trim() ? 'Title is required' : null),
            amount: (value: number) => (value <= 0 ? 'Amount must be greater than 0' : null),
            paid_by_id: (value: string) => (!value ? 'Payer is required' : null),
        },
    });

    // Load expense data when editing
    useEffect(() => {
        if (isEditing && expense) {
            console.log('[ExpenseForm] Loading data for editing expense:', JSON.stringify(expense, null, 2));

            form.setValues({
                title: expense.description,
                amount: expense.amount,
                date: new Date(expense.date),
                due_date: expense.due_date ? new Date(expense.due_date) : null,
                paid_by_id: expense.paid_by.id,
                split_type: expense.split_type,
                split_details: expense.split_details,
                additional_payers: expense.additional_payers || null,
                category_id: expense.category?.id || '',
                notes: expense.notes || '',
                recurring: expense.recurring,
                recurring_frequency: expense.recurring_frequency || '',
                recurring_end_date: expense.recurring_end_date ? new Date(expense.recurring_end_date) : null,
                receipt_images: [],
                status: (expense.status === 'active' || expense.status === 'draft') ? expense.status : 'active',
            });

            if (expense.receipts && expense.receipts.length > 0) {
                setExistingImages(expense.receipts.map((r: any) => r.url));
            }

            // Set multiple payers state
            if (expense.additional_payers && Object.keys(expense.additional_payers).length > 0) {
                setShowMultiplePayers(true);
            }
        }
    }, [isEditing, expense]);

    // Set initial amount debounced value
    useEffect(() => {
        setAmountDebounced(form.values.amount);
    }, []);

    // Debounce amount changes to avoid excessive updates
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setAmountDebounced(form.values.amount);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [form.values.amount]);

    // File upload handling
    const onDrop = useCallback((acceptedFiles: File[]) => {
        // Only take the first file
        const file = acceptedFiles[0];

        // Remove any existing files first
        previewImages.forEach(url => {
            // Skip revoking external URLs (like those from receipt analysis)
            if (url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        });

        form.setFieldValue('receipt_images', [file]);
        setPreviewImages([URL.createObjectURL(file)]);
    }, [previewImages]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.heic'],
            'application/pdf': ['.pdf']
        },
        maxSize: 10 * 1024 * 1024, // 10MB
        maxFiles: 1,
        multiple: false
    });

    // Toggle additional payer fields
    const toggleMultiplePayers = () => {
        setShowMultiplePayers(!showMultiplePayers);

        if (!showMultiplePayers) {
            // Initialize additional payers object when enabling
            form.setFieldValue('additional_payers', {});
        } else {
            // Clear additional payers when disabling
            form.setFieldValue('additional_payers', null);
        }
    };

    // Handle form submission
    const handleSubmit = async (values: FormValues, asDraft: boolean = false) => {
        if (disableSubmit) return;

        try {
            setIsSubmitting(true);

            // Determine if we're creating a new expense or updating an existing one
            const isCreating = !isEditing;
            const method = isCreating ? 'POST' : 'PUT';
            const endpoint = isCreating
                ? `${env.API_URL}/api/groups/${groupId}/expenses`
                : `${env.API_URL}/api/groups/${groupId}/expenses/${expense?.id}`;

            // Log what we're about to submit
            console.log('[ExpenseForm] Submitting expense with data:', {
                title: values.title,
                amount: values.amount,
                paid_by_id: values.paid_by_id,
                split_type: values.split_type
            });

            // Special handling for editing expenses to ensure balances are properly recalculated
            if (isEditing && expense?.id) {
                // First delete the existing expense to revert all balance changes
                console.log('[ExpenseForm] Editing expense - first deleting to recalculate balances');

                // Delete the expense first to reset balances
                try {
                    const deleteResponse = await fetch(
                        `${env.API_URL}/api/groups/${groupId}/expenses/${expense.id}`,
                        {
                            method: 'DELETE',
                            headers: {
                                'Authorization': `Bearer ${accessToken}`,
                            },
                        }
                    );

                    if (!deleteResponse.ok) {
                        throw new Error(`Failed to reset expense for recalculation: ${deleteResponse.status}`);
                    }

                    console.log('[ExpenseForm] Successfully reset expense for recalculation');

                    // Now create a new expense with the updated data using JSON format
                    const createResponse = await fetch(
                        `${env.API_URL}/api/groups/${groupId}/expenses`,
                        {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${accessToken}`,
                                'Content-Type': 'application/json',
                                // Add custom headers to indicate this is an edit operation
                                'X-Expense-Operation': 'edit',
                                'X-Original-Expense-Id': expense.id.toString(),
                            },
                            body: JSON.stringify({
                                title: values.title,
                                description: values.notes || "",
                                amount: values.amount,
                                date: formatDateForApi(values.date),
                                due_date: values.due_date ? formatDateForApi(values.due_date) : null,
                                paid_by_id: parseInt(values.paid_by_id),
                                split_type: values.split_type,
                                split_details: values.split_details,
                                status: asDraft ? 'draft' : values.status,
                                category_id: values.category_id ? parseInt(values.category_id) : null,
                                currency_code: "USD",
                                is_recurring: values.recurring,
                                recurrence_pattern: values.recurring ? (values.recurring_frequency || null) : null,
                                recurrence_end_date: values.recurring && values.recurring_end_date
                                    ? formatDateForApi(values.recurring_end_date)
                                    : null,
                                additional_payers: showMultiplePayers && values.additional_payers ? values.additional_payers : null,
                                merchant_name: null
                            }),
                        }
                    );

                    if (!createResponse.ok) {
                        throw new Error(`Failed to recreate expense: ${createResponse.status}`);
                    }

                    console.log('[ExpenseForm] Successfully recreated expense with updated data');

                    // Handle receipt images if any
                    if (values.receipt_images.length > 0) {
                        // Get the new expense ID from the response
                        const newExpense = await createResponse.json();

                        // Upload images to the new expense
                        for (const file of values.receipt_images) {
                            const imageFormData = new FormData();
                            imageFormData.append('file', file);

                            await fetch(
                                `${env.API_URL}/api/groups/${groupId}/expenses/${newExpense.id}/expense-image`,
                                {
                                    method: 'POST',
                                    headers: {
                                        'Authorization': `Bearer ${accessToken}`,
                                    },
                                    body: imageFormData,
                                }
                            );
                        }
                    }

                    // Handle success - change the message to "updated" instead of "added"
                    notifications.show({
                        title: 'Success',
                        message: 'Expense updated successfully',
                        color: 'green',
                    });

                    onSuccess();
                    onClose();
                    return; // Exit early since we've handled everything
                } catch (error) {
                    console.error('[ExpenseForm] Error during delete/create flow:', error);
                    notifications.show({
                        title: 'Error',
                        message: 'Failed to update expense. Please try again.',
                        color: 'red',
                    });
                    setIsSubmitting(false);
                    return; // Exit on error
                }
            }

            // Standard create/update flow
            const response = await fetch(endpoint, {
                method,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    title: values.title,
                    description: values.notes || "",
                    amount: values.amount,
                    date: formatDateForApi(values.date),
                    due_date: values.due_date ? formatDateForApi(values.due_date) : null,
                    paid_by_id: parseInt(values.paid_by_id),
                    split_type: values.split_type,
                    split_details: values.split_details,
                    status: asDraft ? 'draft' : values.status,
                    category_id: values.category_id ? parseInt(values.category_id) : null,
                    currency_code: "USD",
                    is_recurring: values.recurring,
                    recurrence_pattern: values.recurring ? (values.recurring_frequency || null) : null,
                    recurrence_end_date: values.recurring && values.recurring_end_date
                        ? formatDateForApi(values.recurring_end_date)
                        : null,
                    additional_payers: showMultiplePayers && values.additional_payers ? values.additional_payers : null,
                    merchant_name: null
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to ${isCreating ? 'create' : 'update'} expense: ${response.status}`);
            }

            // Upload receipt images if any
            if (values.receipt_images.length > 0) {
                // For a new expense, get the ID from the response
                let expenseId = expense?.id;
                if (isCreating) {
                    const data = await response.json();
                    expenseId = data.id;
                }

                // Upload each image
                for (const file of values.receipt_images) {
                    const imageFormData = new FormData();
                    imageFormData.append('file', file);

                    await fetch(
                        `${env.API_URL}/api/groups/${groupId}/expenses/${expenseId}/expense-image`,
                        {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${accessToken}`,
                            },
                            body: imageFormData,
                        }
                    );
                }
            }

            notifications.show({
                title: 'Success',
                message: values.status === 'draft'
                    ? 'Expense saved as draft'
                    : (isEditing ? 'Expense updated successfully' : 'Expense added successfully'),
                color: 'green',
            });

            onSuccess();
            onClose();
        } catch (error) {
            console.error('[ExpenseForm] Error saving expense:', error);

            // Enhanced error presentation with more detail
            const errorMessage = error instanceof Error
                ? error.message
                : 'Unexpected error occurred during expense submission';

            // Add suggestions for common errors
            let suggestion = '';
            const errorStr = String(error);
            if (errorStr.includes('network') || errorStr.includes('connection')) {
                suggestion = ' Please check your internet connection and try again.';
            } else if (errorStr.includes('transaction') || errorStr.includes('database')) {
                suggestion = ' The server is experiencing database issues. Please try again in a moment.';
            }

            notifications.show({
                title: 'Error',
                message: errorMessage + suggestion,
                color: 'red',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Save as draft
    const handleSaveAsDraft = () => {
        handleSubmit(form.values, true);
    };

    // Update calculator value
    const handleCalculatorResult = (result: number) => {
        form.setFieldValue('amount', result);
        setCalculatorOpen(false);
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
            <Stack gap="md">
                {/* Basic expense information section */}
                <Paper p="md" withBorder shadow="xs" radius="md">
                    <Stack gap="md">
                        <TextInput
                            label="Title"
                            placeholder="e.g., Dinner, Rent, Groceries"
                            required
                            size="md"
                            {...form.getInputProps('title')}
                        />

                        <Group grow align="flex-start">
                            <div style={{ position: 'relative' }}>
                                <NumberInput
                                    label="Amount"
                                    placeholder="0.00"
                                    required
                                    min={0.01}
                                    step={0.01}
                                    size="md"
                                    rightSection={
                                        <Tooltip label="Use calculator">
                                            <ActionIcon onClick={() => setCalculatorOpen(!calculatorOpen)} size="sm">
                                                <img src={calculatorIcon} alt="Calculator" width={14} />
                                            </ActionIcon>
                                        </Tooltip>
                                    }
                                    {...form.getInputProps('amount')}
                                />
                                {calculatorOpen && (
                                    <Calculator
                                        onCalculate={handleCalculatorResult}
                                        onClose={() => setCalculatorOpen(false)}
                                    />
                                )}
                            </div>

                            <DatePickerInput
                                label="Date"
                                placeholder="When was this expense?"
                                required
                                clearable={false}
                                size="md"
                                {...form.getInputProps('date')}
                            />
                        </Group>

                        <DatePickerInput
                            label="Due Date (Optional)"
                            placeholder="When is it due?"
                            clearable
                            minDate={new Date()}
                            size="md"
                            rightSection={
                                <img src={clockIcon} alt="Clock" width={14} />
                            }
                            {...form.getInputProps('due_date')}
                        />
                    </Stack>
                </Paper>

                {/* Payment section */}
                <Paper p="md" withBorder shadow="xs" radius="md">
                    <Stack gap="md">
                        <Group align="flex-end" grow>
                            <Select
                                label="Paid by"
                                placeholder="Select who paid"
                                required
                                size="md"
                                data={members
                                    // Allow active and pending (implicitly includes ghost if they aren't removed)
                                    .filter(member => member.status !== 'removed')
                                    .map(member => ({
                                        value: String(member.id),
                                        label: member.display_name || member.email,
                                        image: member.avatar_url,
                                    }))}
                                rightSection={
                                    <img src={userIcon} alt="User" width={14} />
                                }
                                {...form.getInputProps('paid_by_id')}
                            />

                            <Box style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <Button
                                    variant={showMultiplePayers ? "filled" : "outline"}
                                    color={showMultiplePayers ? "blue" : "gray"}
                                    leftSection={<img src={multiplePayersIcon} alt="Multiple Payers" width={16} />}
                                    onClick={toggleMultiplePayers}
                                    size="sm"
                                    style={{ marginBottom: 3 }}
                                >
                                    Multiple Payers
                                </Button>
                            </Box>
                        </Group>

                        {showMultiplePayers && (
                            <Box mt="xs">
                                <AdditionalPayersControl
                                    form={form}
                                    members={members}
                                />
                            </Box>
                        )}

                        <CategorySelector
                            groupId={groupId}
                            accessToken={accessToken}
                            value={form.values.category_id}
                            onChange={(value: string) => form.setFieldValue('category_id', value)}
                        />
                    </Stack>
                </Paper>

                {/* Split section */}
                <Paper p="md" withBorder shadow="xs" radius="md">
                    <Stack gap="md">
                        <Text fw={500} size="sm">Split Details</Text>
                        <SplitControls
                            form={form}
                            members={members}
                            setSplitTypeTouched={setSplitTypeTouched}
                            amountDebounced={amountDebounced}
                        />
                    </Stack>
                </Paper>

                {/* Additional options section */}
                <Paper p="md" withBorder shadow="xs" radius="md">
                    <Stack gap="md">
                        <Group>
                            <Switch
                                label={<Text fw={500}>Recurring expense</Text>}
                                size="md"
                                {...form.getInputProps('recurring', { type: 'checkbox' })}
                            />
                        </Group>

                        {form.values.recurring && (
                            <Group grow align="flex-start">
                                <Select
                                    label="Frequency"
                                    placeholder="How often?"
                                    data={[
                                        { value: 'weekly', label: 'Weekly' },
                                        { value: 'monthly', label: 'Monthly' },
                                        { value: 'yearly', label: 'Yearly' },
                                    ]}
                                    {...form.getInputProps('recurring_frequency')}
                                />

                                <DatePickerInput
                                    label="End date (optional)"
                                    placeholder="When to stop?"
                                    clearable
                                    minDate={new Date()}
                                    {...form.getInputProps('recurring_end_date')}
                                />
                            </Group>
                        )}

                        <Textarea
                            label="Notes"
                            placeholder="Optional notes about the expense"
                            minRows={2}
                            {...form.getInputProps('notes')}
                        />
                    </Stack>
                </Paper>

                {/* Receipt section */}
                <Paper p="md" withBorder shadow="xs" radius="md">
                    <Stack gap="md">
                        <Text fw={500} size="sm">Receipt Image</Text>
                        <ImageUploader
                            getRootProps={getRootProps}
                            getInputProps={getInputProps}
                            previewImages={previewImages}
                            existingImages={existingImages}
                            setPreviewImages={setPreviewImages}
                            setExistingImages={setExistingImages}
                            setRemovedImageIds={setRemovedImageIds}
                            expense={expense}
                            form={form}
                        />
                    </Stack>
                </Paper>

                {/* Form actions */}
                <Group justify="flex-end" mt="md">
                    <Button variant="subtle" onClick={onClose}>
                        Cancel
                    </Button>

                    <Button
                        variant="outline"
                        leftSection={<img src={saveIcon} alt="Save" width={14} />}
                        onClick={handleSaveAsDraft}
                    >
                        Save as Draft
                    </Button>

                    <Button type="submit" size="md">
                        {isEditing ? 'Update Expense' : 'Add Expense'}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}; 